/**
 * Which data the app is showing: the invented dataset, or real clients.
 *
 * Only a demo build can switch. A production build has no dataset compiled in
 * and is always live, so the whole of this collapses to `'live'` there — the
 * checks below read the env literal so Rollup can fold them away.
 *
 * The default is `mock`, and deliberately so: the demo is a PUBLIC URL. A
 * reader who arrives with no query string must get invented people, never a
 * login prompt that implies real records are behind it.
 *
 * Switching reloads. Demo mode is installed by patching window.fetch and
 * PIPEDB_URL is resolved once at module load, so flipping in place would
 * leave half the app pointed at the wrong backend.
 */
export type DataMode = 'mock' | 'live';

const PARAM = 'data';
const KEY = 'sizewall.datamode';

export const isDemoBuild = Boolean(import.meta.env.VITE_DEMO_MODE);

let resolved: DataMode | null = null;

export const dataMode = (): DataMode => {
    if (resolved) return resolved;
    if (!isDemoBuild) return (resolved = 'live');

    let mode: DataMode = 'mock';
    try {
        const asked = new URLSearchParams(window.location.search).get(PARAM);
        if (asked === 'live' || asked === 'mock') {
            mode = asked;
            sessionStorage.setItem(KEY, mode);
        } else {
            const stored = sessionStorage.getItem(KEY);
            if (stored === 'live') mode = 'live';
        }
    } catch {
        // Private browsing refuses sessionStorage; mock is the safe default.
    }
    return (resolved = mode);
};

export const isMockData = () => dataMode() === 'mock';

/**
 * Sticky for the tab, and gone when it closes.
 *
 * The switch lands on the roster rather than reloading where you stood. Every
 * id on a client page belongs to one dataset: flipping to live while sitting
 * on /dashboard/demo-daniel asked the real API for an invented person, got the
 * ordinary 404 that means "no such client on this account", and showed it as
 * a dead end. The two datasets share no ids and never will, so the only path
 * guaranteed to exist on the other side is the list itself.
 */
export const setDataMode = (mode: DataMode) => {
    try {
        sessionStorage.setItem(KEY, mode);
    } catch {
        // Fall through to the query string, which does not need storage.
    }
    const url = new URL(window.location.href);
    url.pathname = '/overview';
    url.search = '';
    url.hash = '';
    url.searchParams.set(PARAM, mode);
    window.location.href = url.toString();
};
